<template>
  <div class="text-left">
    <v-row>
      <v-col cols="12">
        <h3 class="mt-15 mb-0">
          {{ $t("title.articleData") }}
        </h3>
      </v-col>
    </v-row>
    <div class="required-label">
      <h5 class="mb-2">{{ $t("form.article.journal") }}</h5>
      <v-chip x-small outlined color="primary">
        {{ $t("form.mandatory") }}
      </v-chip>
    </div>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-select
          outlined
          v-model="publication.article.journal"
          :label="''"
          :placeholder="$t('form.article.journal')"
          :rules="rules.fieldRequired"
          :items="journals"
          item-text="title"
          item-value="id"
          :readonly="disabledForm"
          @change="$emit('handle-change', 'article', 'journal')"
        />
      </v-col>
    </v-row>
    <div class="required-label">
      <h5 class="mb-2">
        {{ $t("form.article.publication_status") }}
      </h5>
      <v-chip x-small outlined color="primary">
        {{ $t("form.mandatory") }}
      </v-chip>
    </div>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-select
          outlined
          v-model="publication.article.publication_status"
          :label="''"
          :placeholder="$t('form.article.publication_status')"
          :rules="rules.fieldRequired"
          :items="publicationStatus"
          item-value="value"
          item-text="text"
          :readonly="disabledForm"
          @change="$emit('handle-change', 'article', 'publication_status')"
        />
      </v-col>
    </v-row>
    <h5>{{ $t("label.dates") }}</h5>
    <v-row>
      <v-col cols="12" sm="4" class="py-0">
        <div class="required-label">
          <h6 class="mb-0">{{ $t('form.article.accepted_date') }}</h6>
          <v-chip x-small outlined color="primary">
            {{ $t("form.mandatory") }}
          </v-chip>
        </div>
        <v-menu
          v-model="menu_accepted_date"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
          :disabled="disabledForm"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              outlined
              v-model="publication.article.accepted_date"
              :label="''"
              :placeholder="$t('form.article.accepted_date')"
              prepend-inner-icon="event"
              readonly
              v-on="on"
              :rules="rules.fieldRequired"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="publication.article.accepted_date"
            @input="menu_accepted_date = false"
            :readonly="disabledForm"
            @change="$emit('handle-change', 'article', 'accepted_date')"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" sm="4" class="py-0">
        <h6 class="mb-2">
          {{ $t('form.article.online_publication_date') }}
        </h6>
        <v-menu
          v-model="menu_publication_date"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
          :disabled="disabledForm"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              outlined
              v-model="publication.article.online_publication_date"
              prepend-inner-icon="event"
              readonly
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="publication.article.online_publication_date"
            @input="menu_publication_date = false"
            :readonly="disabledForm"
            @change="$emit('handle-change', 'article', 'online_publication_date')"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" sm="4" class="py-0">
        <h6 class="mb-2">
          {{ $t('form.article.final_publication_date') }}
        </h6>
        <v-menu
          v-model="menu_final_publication_date"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
          :disabled="disabledForm"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              outlined
              v-model="publication.article.final_publication_date"
              prepend-inner-icon="event"
              readonly
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="publication.article.final_publication_date"
            @input="menu_final_publication_date = false"
            :readonly="disabledForm"
            @change="$emit('handle-change', 'article', 'final_publication_date')"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <h5 class="form-section-title">{{ $t("form.article.journal_location") }}</h5>
    <v-row>
      <v-col cols="12" sm="6" lg="3" class="py-0">
        <v-text-field
          v-model="publication.article.journal_location.issue_number"
          :label="$t('form.article.issueNumber')"
          required
          outlined
          :readonly="disabledForm"
          @change="$emit('handle-change', 'article', 'journal_location')"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" lg="3" class="py-0">
        <v-text-field
          v-model="publication.article.journal_location.volume"
          :label="$t('form.article.volume')"
          required
          outlined
          :readonly="disabledForm"
          @change="$emit('handle-change', 'article', 'journal_location')"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" lg="3" class="py-0">
        <v-text-field
          v-model="publication.article.journal_location.page_number"
          :label="$t('form.article.pageNumber')"
          required
          outlined
          :readonly="disabledForm"
          @change="$emit('handle-change', 'article', 'journal_location')"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" lg="3" class="py-0">
        <v-text-field
          v-model="publication.article.journal_location.pages"
          :label="$t('form.article.pages')"
          required
          outlined
          :readonly="disabledForm"
          @change="$emit('handle-change', 'article', 'journal_location')"
        ></v-text-field>
      </v-col>
    </v-row>
    <div class="required-label">
      <h5 class="mb-2">
        {{ $t("form.article.feder_acknowledgements") }}
      </h5>
      <v-chip x-small outlined color="primary">
        {{ $t("form.mandatory") }}
      </v-chip>
    </div>
    <v-row>
      <v-col>
        <v-checkbox
          class="big"
          v-model="publication.article.feder_acknowledgements"
          :label="$t('form.federAcknowledgementsDetail')"
          :readonly="disabledForm"
          :true-value="true"
          :false-value="false"
          :indeterminate="publication.article.feder_acknowledgements === ''"
          :rules="rules.checkboxRequired"
          @change="$emit('handle-change', 'article', 'feder_acknowledgements')"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import validationRules from "../../../mixins/validationRules";
import axios from "axios";
import publication_model from "../../../mixins/model_utils/publication_model";

export default {
  mixins: [validationRules, publication_model],
  name: "ModifyArticle",
  props: {
    disabledForm: Boolean,
    publication: Object
  },
  data: () => ({
    menu_accepted_date: false,
    menu_publication_date: false,
    menu_final_publication_date: false,
    publicationStatus: [],
    journals: []
  }),

  created() {
    this.getPublicationStatus();
    this.getJournals();
  },

};
</script>

<style scoped>
.required-label {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.required-label .v-chip {
  margin-left: 8px;
}

.required-label h6 {
  margin-bottom: 0;
  font-size: 0.9rem;
}

.form-section-title {
  margin-bottom: 0.85rem;
}
</style>
